import { classNames } from "primereact/utils";
import styles from "./DefaultButton.module.scss";
import { useNavigate } from "react-router-dom";
import { useAppContext } from "../../../../AppContext";
import { Tooltip } from "react-tooltip";
import { RenderWhen } from "../../../infrastructure/components/RenderWhen";
import { useSafeId } from "../../tooltip/hooks/useSafeId";

interface IDefaultButton {
  className?: string;
  design?: "default" | "secondary" | "outline" | "neutral" | "accent" | "link";
  label: string;
  type?: "submit" | "reset" | "button" | undefined;
  disabled?: boolean;
  backgroundColor?: string;
  background?: string;
  textColor?: string;
  href?: string;
  width?: string | number;
  loading?: boolean;
  tooltip?: string | React.ReactNode;
  onClick?: () => void;
  onPointerDown?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerUp?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onPointerLeave?: (event: React.PointerEvent<HTMLButtonElement>) => void;
  onContextMenu?: (event: React.MouseEvent<HTMLButtonElement>) => void;
}

export const DefaultButton = ({
  className,
  design = "default",
  label,
  type = "button",
  disabled = false,
  backgroundColor,
  background,
  textColor,
  width,
  href,
  tooltip,
  loading = false,
  onClick,
  onPointerDown,
  onPointerUp,
  onPointerLeave,
  onContextMenu,
}: IDefaultButton) => {
  const navigate = useNavigate();
  const { isDesktopOrTablet } = useAppContext();
  const tooltipId = useSafeId();

  if (!width) {
    if (isDesktopOrTablet) {
      width = "300px";
    } else {
      width = "90%";
    }
  }

  // Convert design name to CSS class name (handle dashes)
  const getCssClassName = (design: string) => {
    switch (design) {
      case "neutral":
        return styles.wizardBack;
      case "accent":
        return styles.wizardForward;
      case "link":
        return styles.link;
      default:
        return styles[design];
    }
  };

  const designClassName = getCssClassName(design);

  return (
    <button
      style={{ width: width, ...(backgroundColor !== undefined && { backgroundColor }), ...(background !== undefined && { background }), color: textColor }}
      className={classNames(
        styles.button,
        tooltipId,
        designClassName,
        className
      )}
      type={type}
      onClick={href && !disabled && !loading ? () => navigate(href) : onClick}
      onPointerDown={onPointerDown}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
      onContextMenu={onContextMenu}
      disabled={disabled || loading}
    >
      <span className={styles.button_contents}>
        {label}
        {loading && <span className={styles.spinner} />}
      </span>

      <RenderWhen condition={!!tooltip && tooltip !== undefined}>
        <Tooltip
          anchorSelect={`.${tooltipId}`}
          place="top"
          className={styles.root_tooltip}
        >
          {tooltip}
        </Tooltip>
      </RenderWhen>
    </button>
  );
};
